import { computed, ref, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import Api from '@/common/api';
import type { TransStatement } from '@/common/api/model';
import { TransactionType } from '@/common/config';

export interface PaymentTransactionsParams {
  transactionType: TransactionType.Deposit | TransactionType.Withdrawal;
  pageSize?: number;
  pageNo?: number;
  statuses?: string;
}

export interface PaymentTransactionsResponse {
  statements: TransStatement[];
  totalNum: number;
  pageNo: number;
  pageSize: number;
}

/**
 * Fetch payment transactions (deposits/withdrawals) from the API
 */
const fetchPaymentTransactions = async (params: PaymentTransactionsParams): Promise<PaymentTransactionsResponse> => {
  const { transactionType, pageSize = 10, pageNo = 1, statuses } = params;

  if (localStorage.getItem('failedTxFlow') === 'true') {
    throw new Error('Failed to fetch payment transactions');
  }

  const response = await Api.getTransactionHistory({
    type: transactionType,
    pageSize,
    pageNo,
    statuses,
    _t: Date.now(),
  });

  return {
    statements: response.data.statements || [],
    totalNum: response.data.totalNum || 0,
    pageNo: response.data.pageNo || pageNo,
    pageSize: response.data.pageSize || pageSize,
  };
};

export interface UsePaymentTransactionsOptions {
  /** Whether the query should be enabled (default: true when transactionType is set) */
  enabled?: Ref<boolean>;
}

/**
 * TanStack Query hook to fetch payment transactions
 * Provides automatic caching, loading states, and error handling
 *
 * @param params - Ref containing the query parameters
 * @param options - Optional configuration for the query
 * @returns TanStack Query result with payment transactions data
 *
 * @example
 * ```ts
 * const params = ref({ transactionType: TransactionType.Deposit, pageSize: 10 });
 * const { data, isLoading, error, refetch } = usePaymentTransactions(params);
 *
 * // With custom enabled option
 * const { data } = usePaymentTransactions(params, { enabled: computed(() => !isMocked.value) });
 * ```
 */
export const usePaymentTransactions = (
  params: Ref<PaymentTransactionsParams>,
  options?: UsePaymentTransactionsOptions
) => {
  const defaultEnabled = computed(() => !!params.value.transactionType);
  const enabled = computed(() => {
    if (options?.enabled) {
      return defaultEnabled.value && options.enabled.value;
    }
    return defaultEnabled.value;
  });

  return useQuery({
    queryKey: ['paymentTransactions', params],
    queryFn: () => fetchPaymentTransactions(params.value),
    enabled,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
  });
};
